import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Grid, Alert, Button, TextField, InputAdornment,
    FormControl, Select, MenuItem, InputLabel, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TablePagination, Skeleton,
    Avatar, Drawer, IconButton, Divider, Chip, Dialog, DialogTitle,
    DialogContent, DialogActions, CircularProgress,
} from '@mui/material';
import {
    Search as SearchIcon,
    Close as CloseIcon,
    People as PeopleIcon,
    CheckCircle as CheckCircleIcon,
    VerifiedUser as VerifiedIcon,
    Block as BlockIcon,
} from '@mui/icons-material';
import AdminSidebar from './components/AdminSidebar';
import KPIStatCard from './components/KPIStatCard';
import UserProfileView from './components/UserProfileView';
import { adminApi, AdminUser } from '../../api/adminApi';
import { adminTokens as T } from '../../theme/adminTokens';

const DRAWER_WIDTH = 260;
const DETAIL_DRAWER_WIDTH = 480;

interface FilterState {
    search: string;
    status: string;
    plan: string;
    gender: string;
    verified: string;
    warningScope: 'all' | 'mine';
}

const initialFilters: FilterState = {
    search: '',
    status: 'all',
    plan: 'all',
    gender: 'all',
    verified: 'all',
    warningScope: 'all',
};

const statusToTone = (s: AdminUser['status']) => {
    switch (s) {
        case 'active':     return { bg: T.color.success.soft, text: T.color.success.text, label: 'Active' };
        case 'paused':     return { bg: T.color.surface.sunken, text: T.color.text.secondary, label: 'Paused' };
        case 'warned':     return { bg: T.color.warning.soft, text: T.color.warning.text, label: 'Warned' };
        case 'suspended':  return { bg: T.color.warning.soft, text: T.color.warning.text, label: 'Suspended' };
        case 'banned':     return { bg: T.color.error.soft,   text: T.color.error.text,   label: 'Banned' };
        case 'incomplete': return { bg: T.color.info.soft,    text: T.color.info.text,    label: 'Incomplete' };
        default:           return { bg: T.color.surface.sunken, text: T.color.text.secondary, label: s };
    }
};

const planLabel = (u: AdminUser) => {
    if (u.subscription_tier && u.subscription_tier !== 'free') {
        return u.subscription_tier.charAt(0).toUpperCase() + u.subscription_tier.slice(1);
    }
    if (u.subscription_status && u.subscription_status !== 'free') {
        return u.subscription_status.charAt(0).toUpperCase() + u.subscription_status.slice(1);
    }
    return 'Free';
};

const initialsFrom = (u: AdminUser) => {
    const src = u.full_name || u.email || '';
    const parts = src.trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || 'U';
};

const formatDate = (s: string | null) => {
    if (!s) return '—';
    return new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatRelative = (s: string | null) => {
    if (!s) return 'Never';
    const ms = Date.now() - new Date(s).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return formatDate(s);
};

const StatusBadge: React.FC<{ status: AdminUser['status'] }> = ({ status }) => {
    const tone = statusToTone(status);
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center',
            px: `${T.space[2]}px`, py: `2px`,
            borderRadius: `${T.radius.pill}px`,
            bgcolor: tone.bg, color: tone.text,
            fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold,
            lineHeight: 1.4,
        }}>{tone.label}</Box>
    );
};

const PlanBadge: React.FC<{ label: string }> = ({ label }) => {
    const isPaid = label !== 'Free';
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center',
            px: `${T.space[2]}px`, py: `2px`,
            borderRadius: `${T.radius.pill}px`,
            bgcolor: isPaid ? T.color.brand[50] : T.color.surface.sunken,
            color: isPaid ? T.color.brand[600] : T.color.text.secondary,
            fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold,
            lineHeight: 1.4,
        }}>{label}</Box>
    );
};

const UserManagementScreen: React.FC = () => {
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [filters, setFilters] = useState<FilterState>(initialFilters);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
    const [profileViewOpen, setProfileViewOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detail, setDetail] = useState<any>(null);
    const [reloadNonce, setReloadNonce] = useState(0);
    const [moderationAction, setModerationAction] = useState<'clear_warning' | 'reinstate' | null>(null);
    const [moderationReason, setModerationReason] = useState('');
    const [moderationSaving, setModerationSaving] = useState(false);
    const [moderationError, setModerationError] = useState<string | null>(null);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(filters.search), 350);
        return () => clearTimeout(t);
    }, [filters.search]);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const resp = await adminApi.listUsers({
                    page,
                    limit: rowsPerPage,
                    search: debouncedSearch,
                    status: filters.status,
                    plan: filters.plan,
                    gender: filters.gender,
                    verified: filters.verified,
                    warningScope: filters.warningScope,
                });
                if (!active) return;
                setUsers(resp.data || []);
                setTotal(resp.pagination?.total || 0);
            } catch (err: any) {
                if (!active) return;
                setError(err.message || 'Failed to load users');
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [page, rowsPerPage, debouncedSearch, filters.status, filters.plan, filters.gender, filters.verified, filters.warningScope, reloadNonce]);

    const handleFilterChange = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
        setFilters(f => ({ ...f, [key]: value }));
        setPage(0);
    };

    const handleStatusChange = (value: string) => {
        const isWarningList = value === 'warned' || value === 'warning_history';
        setFilters(f => ({
            ...f,
            status: value,
            warningScope: isWarningList ? 'mine' : 'all',
        }));
        setPage(0);
    };

    const openUserDetail = async (user: AdminUser) => {
        setSelectedUser(user);
        setDetail(null);
        try {
            setDetailLoading(true);
            const resp = await adminApi.getUserProfileById(user.id);
            setDetail(resp.data || null);
        } catch {
            setDetail(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const closeUserDetail = () => {
        setSelectedUser(null);
        setDetail(null);
    };

    const openModerationAction = (action: 'clear_warning' | 'reinstate') => {
        setModerationAction(action);
        setModerationReason('');
        setModerationError(null);
    };

    const closeModerationAction = () => {
        if (moderationSaving) return;
        setModerationAction(null);
        setModerationReason('');
        setModerationError(null);
    };

    const submitModerationAction = async () => {
        if (!selectedUser || !moderationAction || !moderationReason.trim()) {
            setModerationError('Please provide a reason for this decision.');
            return;
        }

        try {
            setModerationSaving(true);
            setModerationError(null);
            let response: any;
            if (moderationAction === 'clear_warning') {
                response = await adminApi.clearUserWarning({
                    reportedUserEmail: selectedUser.email,
                    reason: moderationReason.trim(),
                });
            } else {
                response = await adminApi.reinstateUser({
                    reportedUserEmail: selectedUser.email,
                    reason: moderationReason.trim(),
                });
            }

            const detailResponse = await adminApi.getUserProfileById(selectedUser.id);
            setDetail(detailResponse.data || null);
            setSelectedUser(user => user ? { ...user, status: 'active', is_active: true } : user);
            setReloadNonce(n => n + 1);
            setModerationAction(null);
            setModerationReason('');
            setModerationError(null);
            if (response?.data?.enforcementStatus === 'database_only') {
                setError(response.message || 'The database status changed, but the authentication restriction needs review.');
            }
        } catch (err: any) {
            setModerationError(err.message || 'Failed to update moderation status.');
        } finally {
            setModerationSaving(false);
        }
    };

    const kpis = useMemo(() => {
        const active = users.filter(u => u.status === 'active').length;
        const verified = users.filter(u => u.verified).length;
        const banned = users.filter(u => u.status === 'banned' || u.status === 'suspended').length;
        return { active, verified, banned };
    }, [users]);

    return (
        <Box sx={{ display: 'flex', bgcolor: T.color.surface.page, minHeight: '100vh' }}>
            <AdminSidebar />
            <Box component="main" sx={{
                flexGrow: 1,
                p: { xs: `${T.space[4]}px`, md: `${T.space[6]}px` },
                minWidth: 0,
                overflow: 'hidden',
            }}>
                {/* Page header */}
                <Box sx={{ mb: `${T.space[6]}px` }}>
                    <Typography sx={{
                        fontSize: T.type.size['2xl'], fontWeight: T.type.weight.bold,
                        color: T.color.text.primary, lineHeight: T.type.lineHeight.tight,
                    }}>User management</Typography>
                    <Typography sx={{ fontSize: T.type.size.md, color: T.color.text.secondary, mt: `${T.space[1]}px` }}>
                        Search, filter, and act on user accounts across the platform.
                    </Typography>
                </Box>

                {/* KPIs */}
                <Grid container spacing={3} sx={{ mb: `${T.space[5]}px` }}>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard label="Total users" value={total} icon={<PeopleIcon />} tone="info" loading={loading} />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard label="Active on this page" value={kpis.active} icon={<CheckCircleIcon />} tone="success" loading={loading} />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard label="Verified on this page" value={kpis.verified} icon={<VerifiedIcon />} tone="neutral" loading={loading} />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard label="Restricted on this page" value={kpis.banned} icon={<BlockIcon />} tone="error" loading={loading} />
                    </Grid>
                </Grid>

                {/* Filter bar */}
                <Box sx={{
                    bgcolor: T.color.surface.raised,
                    border: `1px solid ${T.color.surface.border}`,
                    borderRadius: `${T.radius.lg}px`,
                    p: `${T.space[4]}px`,
                    mb: `${T.space[4]}px`,
                    boxShadow: T.shadow.sm,
                    display: 'flex', flexWrap: 'wrap', gap: `${T.space[3]}px`, alignItems: 'center',
                }}>
                    <TextField
                        placeholder="Search by name or email…"
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        size="small"
                        sx={{ minWidth: 260, flex: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: T.color.text.tertiary, fontSize: 18 }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Status</InputLabel>
                        <Select value={filters.status} label="Status" onChange={(e) => handleStatusChange(e.target.value)}>
                            <MenuItem value="all">All statuses</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="incomplete">Incomplete</MenuItem>
                            <MenuItem value="warned">Current warnings</MenuItem>
                            <MenuItem value="warning_history">Warning history</MenuItem>
                            <MenuItem value="suspended">Suspended</MenuItem>
                            <MenuItem value="banned">Banned</MenuItem>
                        </Select>
                    </FormControl>
                    {(filters.status === 'warned' || filters.status === 'warning_history') && (
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Issued by</InputLabel>
                            <Select
                                value={filters.warningScope}
                                label="Issued by"
                                onChange={(e) => handleFilterChange('warningScope', e.target.value as 'all' | 'mine')}
                            >
                                <MenuItem value="mine">My warnings</MenuItem>
                                <MenuItem value="all">All admins</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Plan</InputLabel>
                        <Select value={filters.plan} label="Plan" onChange={(e) => handleFilterChange('plan', e.target.value)}>
                            <MenuItem value="all">All plans</MenuItem>
                            <MenuItem value="free">Free</MenuItem>
                            <MenuItem value="golden">Golden</MenuItem>
                            <MenuItem value="platinum">Platinum</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Gender</InputLabel>
                        <Select value={filters.gender} label="Gender" onChange={(e) => handleFilterChange('gender', e.target.value)}>
                            <MenuItem value="all">All genders</MenuItem>
                            <MenuItem value="male">Male</MenuItem>
                            <MenuItem value="female">Female</MenuItem>
                            <MenuItem value="nonbinary">Non-binary</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Verified</InputLabel>
                        <Select value={filters.verified} label="Verified" onChange={(e) => handleFilterChange('verified', e.target.value)}>
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="true">Verified</MenuItem>
                            <MenuItem value="false">Unverified</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {/* Error */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: `${T.space[4]}px`, borderRadius: `${T.radius.md}px` }}
                        action={<Button color="inherit" size="small" onClick={() => setReloadNonce(n => n + 1)}>Retry</Button>}
                    >
                        {error}
                    </Alert>
                )}

                {/* Table */}
                <Box sx={{
                    bgcolor: T.color.surface.raised,
                    border: `1px solid ${T.color.surface.border}`,
                    borderRadius: `${T.radius.lg}px`,
                    boxShadow: T.shadow.sm,
                    overflow: 'hidden',
                }}>
                    <TableContainer>
                        <Table size="medium">
                            <TableHead>
                                <TableRow sx={{ '& th': { bgcolor: T.color.surface.sunken, color: T.color.text.secondary, fontWeight: T.type.weight.semibold, fontSize: T.type.size.xs, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: `1px solid ${T.color.surface.border}` } }}>
                                    <TableCell>User</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Plan</TableCell>
                                    <TableCell>Country</TableCell>
                                    <TableCell>Warnings</TableCell>
                                    <TableCell>Reports</TableCell>
                                    <TableCell>Joined</TableCell>
                                    <TableCell>Last seen</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    Array.from({ length: 8 }).map((_, i) => (
                                        <TableRow key={i}>
                                            <TableCell colSpan={8}>
                                                <Skeleton variant="text" height={32} />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : users.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: `${T.space[6]}px`, color: T.color.text.tertiary }}>
                                            No users match the current filters.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    users.map(u => (
                                        <TableRow
                                            key={u.id}
                                            hover
                                            onClick={() => openUserDetail(u)}
                                            sx={{
                                                cursor: 'pointer',
                                                '& td': { borderBottom: `1px solid ${T.color.surface.border}`, fontSize: T.type.size.sm },
                                                '&:hover td': { bgcolor: T.color.surface.sunken },
                                            }}
                                        >
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: `${T.space[3]}px` }}>
                                                    <Avatar src={u.avatar_url || undefined} sx={{
                                                        width: 36, height: 36,
                                                        bgcolor: T.color.brand[100], color: T.color.brand[600],
                                                        fontSize: T.type.size.sm, fontWeight: T.type.weight.semibold,
                                                    }}>{initialsFrom(u)}</Avatar>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: `${T.space[1]}px` }}>
                                                            <Typography sx={{ fontSize: T.type.size.sm, fontWeight: T.type.weight.semibold, color: T.color.text.primary }}>
                                                                {u.full_name || 'Unnamed'}
                                                            </Typography>
                                                            {u.verified && <VerifiedIcon sx={{ fontSize: 14, color: T.color.info.base }} />}
                                                        </Box>
                                                        <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>
                                                            {u.email} {u.age ? `· ${u.age}` : ''}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell><StatusBadge status={u.status} /></TableCell>
                                            <TableCell><PlanBadge label={planLabel(u)} /></TableCell>
                                            <TableCell sx={{ color: T.color.text.secondary }}>{u.country || '—'}</TableCell>
                                            <TableCell>
                                                {u.warning_count > 0 ? (
                                                    <Box>
                                                        <Typography sx={{ fontSize: T.type.size.sm, color: T.color.warning.text, fontWeight: T.type.weight.semibold }}>
                                                            {u.warning_count}{u.last_warning_by_me ? ' · yours' : ''}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.tertiary }}>
                                                            {formatRelative(u.last_warning_at)}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography sx={{ color: T.color.text.tertiary }}>—</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ color: u.report_count > 0 ? T.color.error.text : T.color.text.tertiary, fontWeight: u.report_count > 0 ? T.type.weight.semibold : T.type.weight.regular, fontVariantNumeric: 'tabular-nums' }}>
                                                {u.report_count > 0 ? u.report_count : '—'}
                                            </TableCell>
                                            <TableCell sx={{ color: T.color.text.secondary, fontVariantNumeric: 'tabular-nums' }}>{formatDate(u.created_at)}</TableCell>
                                            <TableCell sx={{ color: T.color.text.secondary }}>{formatRelative(u.last_seen)}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Box>
            </Box>

            {/* Detail drawer */}
            <Drawer
                anchor="right"
                open={!!selectedUser}
                onClose={closeUserDetail}
                PaperProps={{ sx: { width: { xs: '100%', sm: DETAIL_DRAWER_WIDTH }, bgcolor: T.color.surface.raised } }}
            >
                {selectedUser && (
                    <Box sx={{ p: `${T.space[5]}px`, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: `${T.space[4]}px` }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: `${T.space[3]}px` }}>
                                <Avatar src={selectedUser.avatar_url || undefined} sx={{
                                    width: 56, height: 56,
                                    bgcolor: T.color.brand[100], color: T.color.brand[600],
                                    fontWeight: T.type.weight.bold, fontSize: T.type.size.lg,
                                }}>{initialsFrom(selectedUser)}</Avatar>
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: `${T.space[1]}px` }}>
                                        <Typography sx={{ fontSize: T.type.size.xl, fontWeight: T.type.weight.bold, color: T.color.text.primary }}>
                                            {selectedUser.full_name || 'Unnamed'}
                                        </Typography>
                                        {selectedUser.verified && <VerifiedIcon sx={{ fontSize: 18, color: T.color.info.base }} />}
                                    </Box>
                                    <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.secondary }}>{selectedUser.email}</Typography>
                                </Box>
                            </Box>
                            <IconButton onClick={closeUserDetail} aria-label="Close detail panel">
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${T.space[2]}px`, mb: `${T.space[4]}px` }}>
                            <StatusBadge status={selectedUser.status} />
                            <PlanBadge label={planLabel(selectedUser)} />
                            {selectedUser.gender && <Chip size="small" label={selectedUser.gender} />}
                            {selectedUser.country && <Chip size="small" label={selectedUser.country} />}
                            {selectedUser.age && <Chip size="small" label={`${selectedUser.age} yrs`} />}
                        </Box>

                        <Button
                            variant="contained"
                            onClick={() => setProfileViewOpen(true)}
                            sx={{ mb: `${T.space[4]}px`, textTransform: 'none', fontWeight: T.type.weight.semibold }}
                        >
                            View profile and photos
                        </Button>

                        <Divider sx={{ mb: `${T.space[4]}px` }} />

                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: `${T.space[3]}px`, mb: `${T.space[4]}px` }}>
                            <DetailField label="User ID" value={selectedUser.id} mono />
                            <DetailField label="Joined" value={formatDate(selectedUser.created_at)} />
                            <DetailField label="Last seen" value={formatRelative(selectedUser.last_seen)} />
                            <DetailField label="Reports" value={String(selectedUser.report_count)} />
                        </Box>

                        {selectedUser.warning_count > 0 && (
                            <Box sx={{
                                p: `${T.space[3]}px`, mb: `${T.space[4]}px`,
                                bgcolor: T.color.warning.soft,
                                border: `1px solid ${T.color.warning.base}`,
                                borderRadius: `${T.radius.md}px`,
                            }}>
                                <Typography sx={{ fontSize: T.type.size.sm, fontWeight: T.type.weight.semibold, color: T.color.warning.text }}>
                                    {selectedUser.warning_count} warning{selectedUser.warning_count === 1 ? '' : 's'} on record
                                </Typography>
                                {selectedUser.last_warning_reason && (
                                    <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.secondary, mt: `${T.space[1]}px` }}>
                                        Latest: {selectedUser.last_warning_reason}
                                    </Typography>
                                )}
                                <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.tertiary, mt: `${T.space[1]}px` }}>
                                    Latest {formatRelative(selectedUser.last_warning_at)}{selectedUser.last_warning_by_me ? ' · includes one issued by you' : ''}
                                </Typography>
                            </Box>
                        )}

                        {selectedUser.status === 'suspended' && selectedUser.moderation_expires_at && (
                            <Alert severity="warning" sx={{ mb: `${T.space[3]}px` }}>
                                Suspended until {new Date(selectedUser.moderation_expires_at).toLocaleString()}.
                            </Alert>
                        )}

                        {selectedUser.status === 'warned' && (
                            <Button
                                variant="outlined"
                                color="success"
                                onClick={() => openModerationAction('clear_warning')}
                                sx={{ mb: `${T.space[3]}px`, textTransform: 'none', fontWeight: T.type.weight.semibold }}
                            >
                                Clear warning after review
                            </Button>
                        )}

                        {(selectedUser.status === 'suspended' || selectedUser.status === 'banned') && (
                            <Button
                                variant="outlined"
                                color="success"
                                onClick={() => openModerationAction('reinstate')}
                                sx={{ mb: `${T.space[3]}px`, textTransform: 'none', fontWeight: T.type.weight.semibold }}
                            >
                                Reinstate account after appeal
                            </Button>
                        )}

                        <Divider sx={{ mb: `${T.space[4]}px` }} />

                        <Typography sx={{ fontSize: T.type.size.sm, fontWeight: T.type.weight.semibold, color: T.color.text.primary, mb: `${T.space[2]}px` }}>
                            Account history
                        </Typography>

                        {detailLoading ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${T.space[2]}px` }}>
                                {[0, 1, 2].map(i => <Skeleton key={i} variant="rectangular" height={42} />)}
                            </Box>
                        ) : detail?.statusHistory?.length ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${T.space[2]}px`, flex: 1, overflowY: 'auto' }}>
                                {detail.statusHistory.slice(0, 12).map((s: any) => (
                                    <Box key={s.id} sx={{
                                        p: `${T.space[3]}px`,
                                        bgcolor: T.color.surface.sunken,
                                        borderRadius: `${T.radius.md}px`,
                                    }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                            <Typography sx={{ fontSize: T.type.size.sm, fontWeight: T.type.weight.semibold, color: T.color.text.primary, textTransform: 'capitalize' }}>
                                                {s.status}
                                            </Typography>
                                            <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.tertiary }}>
                                                {formatRelative(s.created_at)}
                                            </Typography>
                                        </Box>
                                        {s.reason && (
                                            <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.secondary, mt: `${T.space[1]}px` }}>
                                                {s.reason}
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.tertiary }}>
                                No moderation history.
                            </Typography>
                        )}
                    </Box>
                )}
            </Drawer>

            <UserProfileView
                open={profileViewOpen && !!selectedUser}
                onClose={() => setProfileViewOpen(false)}
                userId={selectedUser?.id}
            />

            <Dialog open={!!moderationAction} onClose={closeModerationAction} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {moderationAction === 'clear_warning' ? 'Clear warning' : 'Reinstate account'}
                </DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: `${T.space[3]}px` }}>
                        {moderationAction === 'clear_warning'
                            ? 'Confirm that the reported issue has been reviewed or corrected. The user will return to Daily Picks.'
                            : 'Use this after reviewing an appeal or confirming the restriction is no longer required. Account access will be restored.'}
                    </Alert>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={3}
                        label="Decision reason"
                        value={moderationReason}
                        onChange={(e) => setModerationReason(e.target.value)}
                        error={!!moderationError}
                        helperText={moderationError || 'This reason is saved in the moderation audit trail.'}
                        disabled={moderationSaving}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeModerationAction} disabled={moderationSaving}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={submitModerationAction}
                        disabled={moderationSaving || !moderationReason.trim()}
                        startIcon={moderationSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        {moderationSaving ? 'Saving…' : 'Confirm decision'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const DetailField: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
    <Box>
        <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.tertiary, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: T.type.weight.semibold }}>
            {label}
        </Typography>
        <Typography sx={{
            fontSize: T.type.size.sm, color: T.color.text.primary, mt: `${T.space[1]}px`,
            fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined,
            wordBreak: mono ? 'break-all' : undefined,
        }}>
            {value}
        </Typography>
    </Box>
);

export default UserManagementScreen;
